import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { createNadDebug } from '../../../../utils/debugLog'

const log = createNadDebug('dw:timeline-director-control')
interface Metadata { duration?: number; error?: string }

/** Probe preloaded video blobs, keeping historical outputs and replacements distinct. */
export function useEditSpecSourceDurations(blobs: Readonly<Record<string, string>>) {
  const { t } = useTranslation('canvas')
  const [metadata, setMetadata] = useState<Record<string, Metadata>>({})
  useEffect(() => {
    let active = true
    const videos = [...new Set(Object.values(blobs))].map((url) => {
      const video = document.createElement('video')
      video.preload = 'metadata'
      const finish = (result: Metadata) => {
        if (!active) return
        setMetadata((previous) => ({ ...previous, [url]: result }))
        log({ event: 'source-duration-probed', ...result })
      }
      video.onloadedmetadata = () => {
        if (!Number.isFinite(video.duration) || video.duration <= 0) {
          finish({ error: t('timelineDirector.invalidSourceDuration') })
        } else finish({ duration: video.duration })
      }
      video.onerror = () => finish({ error: video.error?.message || t('timelineDirector.sourceMetadataFailed') })
      video.src = url
      return video
    })
    return () => {
      active = false
      for (const video of videos) {
        video.onloadedmetadata = null
        video.onerror = null
        video.removeAttribute('src')
        video.load()
      }
    }
  }, [blobs, t])
  return useMemo(() => {
    const durations: Record<string, number> = {}
    const errors: string[] = []
    for (const [identity, url] of Object.entries(blobs)) {
      const result = metadata[url]
      if (result?.duration !== undefined) durations[identity] = result.duration
      if (result?.error) errors.push(`${identity}: ${result.error}`)
    }
    return { durations, errors }
  }, [blobs, metadata])
}
